import { storage, AUTH_TOKEN_KEY } from '@/utils/storage';
import { ApiException, createApiError } from './types';
import { runtimeConfig } from '@/config/runtime';

const BASE_URL = runtimeConfig.apiBaseUrl;
const APP_ID = runtimeConfig.appId;
const AUTH_EXPIRED_EVENT = 'auth:expired';

function notifyAuthExpired(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
}

export interface RequestConfig extends RequestInit {
  params?: Record<string, string | number | boolean | undefined>;
  timeout?: number;
}

function extractStringField(
  value: string,
  field: string
): string | undefined {
  const pattern = new RegExp(`['"]${field}['"]\\s*:\\s*['"]([^'"]+)['"]`);
  const match = value.match(pattern);
  return match?.[1];
}

function parseDetailErrorString(detail: string): {
  message?: string;
  code?: string;
} {
  const code = extractStringField(detail, 'id');
  const message = extractStringField(detail, 'message');
  return { message, code };
}

function formatApiErrorMessage(message: string | undefined, code: string | undefined): string | undefined {
  if (code === 'invalid-access-token') {
    return 'Your session has expired. Please sign in again.';
  }

  return message;
}

async function handleResponse<T>(response: Response): Promise<T> {
  const hasStoredToken = Boolean(storage.get<string>(AUTH_TOKEN_KEY));

  const extractErrorFromPayload = (
    payload: unknown
  ): {
    message?: string;
    code?: string;
    details?: Record<string, unknown>;
    hasErrorEnvelope: boolean;
  } => {
    if (!payload || typeof payload !== 'object') {
      return { hasErrorEnvelope: false };
    }

    const record = payload as Record<string, unknown>;
    const directMessage = typeof record.message === 'string' ? record.message : undefined;
    const directDetail = typeof record.detail === 'string' ? record.detail : undefined;
    const parsedDetail = directDetail ? parseDetailErrorString(directDetail) : {};

    const nestedError =
      record.error && typeof record.error === 'object'
        ? (record.error as Record<string, unknown>)
        : undefined;

    const nestedMessage =
      nestedError && typeof nestedError.message === 'string'
        ? nestedError.message
        : undefined;
    const nestedCode =
      nestedError && typeof nestedError.id === 'string' ? nestedError.id : undefined;
    const resultIsError = record.result === 'error';
    const hasNestedError =
      Boolean(nestedError) && (Boolean(nestedCode) || Boolean(nestedMessage));

    const code = nestedCode || parsedDetail.code;
    const rawMessage = directMessage || parsedDetail.message || nestedMessage || directDetail;
    const message = formatApiErrorMessage(rawMessage, code);

    return {
      message,
      code,
      details: record,
      hasErrorEnvelope: hasNestedError || resultIsError || Boolean(parsedDetail.code || parsedDetail.message),
    };
  };

  const shouldExpireSession = (
    status: number,
    code: string | undefined
  ): boolean => {
    if (!hasStoredToken) return false;
    if (status === 401) return true;
    return code === 'invalid-access-token';
  };

  if (!response.ok) {
    let errorMessage = `Request failed: ${response.statusText}`;
    let errorDetails: Record<string, unknown> | undefined;
    let errorCode: string | undefined;

    try {
      const errorBody = await response.json();
      const extracted = extractErrorFromPayload(errorBody);
      if (extracted.message) {
        errorMessage = extracted.message;
      }
      errorCode = extracted.code;
      errorDetails = extracted.details;
    } catch {
      // Response body is not JSON
    }

    if (shouldExpireSession(response.status, errorCode)) {
      notifyAuthExpired();
    }

    throw new ApiException(
      createApiError(
        errorMessage,
        response.status,
        errorCode || response.status.toString(),
        errorDetails
      )
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type');
  if (contentType?.includes('application/json')) {
    const jsonBody = await response.json();
    const extracted = extractErrorFromPayload(jsonBody);

    if (extracted.hasErrorEnvelope) {
      if (shouldExpireSession(response.status, extracted.code)) {
        notifyAuthExpired();
      }

      throw new ApiException(
        createApiError(
          extracted.message || 'Request failed',
          response.status,
          extracted.code,
          extracted.details
        )
      );
    }

    return jsonBody as T;
  }

  return response.blob() as Promise<T>;
}

function buildUrl(
  endpoint: string,
  params?: Record<string, string | number | boolean | undefined>
): string {
  const isAbsolute = endpoint.startsWith('http://') || endpoint.startsWith('https://');
  const hasBaseUrl = Boolean(BASE_URL);
  const origin =
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : 'http://localhost';

  const url = hasBaseUrl
    ? new URL(endpoint, BASE_URL)
    : isAbsolute
      ? new URL(endpoint)
      : new URL(endpoint, origin);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        url.searchParams.append(key, String(value));
      }
    });
  }

  if (!hasBaseUrl && !isAbsolute) {
    return `${url.pathname}${url.search}${url.hash}`;
  }

  return url.toString();
}

function getAuthHeaders(): Record<string, string> {
  const token = storage.get<string>(AUTH_TOKEN_KEY);
  if (token) {
    return { Authorization: `Bearer ${token}` };
  }
  return {};
}

export async function request<T>(
  endpoint: string,
  config: RequestConfig = {}
): Promise<T> {
  const { params, timeout = 30000, ...fetchConfig } = config;

  const url = buildUrl(endpoint, params);

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(),
    ...(fetchConfig.headers as Record<string, string>),
  };

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...fetchConfig,
      headers,
      signal: controller.signal,
    });

    return handleResponse<T>(response);
  } catch (error) {
    if (error instanceof ApiException) {
      throw error;
    }

    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new ApiException(createApiError('Request timeout', 408, 'TIMEOUT'));
      }
      throw new ApiException(createApiError(error.message, 0, 'NETWORK_ERROR'));
    }

    throw new ApiException(createApiError('Unknown error occurred'));
  } finally {
    clearTimeout(timeoutId);
  }
}

export const api = {
  get: <T>(endpoint: string, config?: RequestConfig) =>
    request<T>(endpoint, { ...config, method: 'GET' }),

  post: <T>(endpoint: string, data?: unknown, config?: RequestConfig) =>
    request<T>(endpoint, {
      ...config,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }),

  put: <T>(endpoint: string, data?: unknown, config?: RequestConfig) =>
    request<T>(endpoint, {
      ...config,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    }),

  patch: <T>(endpoint: string, data?: unknown, config?: RequestConfig) =>
    request<T>(endpoint, {
      ...config,
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    }),

  delete: <T>(endpoint: string, config?: RequestConfig) =>
    request<T>(endpoint, { ...config, method: 'DELETE' }),
};

export { APP_ID, BASE_URL };
export { AUTH_EXPIRED_EVENT };
